import { AppErrorCodeEnum } from '../enums/AppErrorCodeEnum';

/**
 * Possible Ant Design notification types for error display.
 */
export type NotificationType = 'error' | 'warning' | 'info' | 'success';

/**
 * Configuration entry describing how to handle a specific application error code.
 */
export interface ErrorHandlingEntry {
  /**
   * i18n key for localized message (e.g., 'errors.genericFetchError')
   */
  i18nKey: string;
  /**
   * Ant Design notification type (error, warning, info, success)
   */
  notificationType: NotificationType;
  /**
   * Whether this error should be logged to Sentry by default
   */
  logToSentryByDefault: boolean;
  /**
   * Default interpolation params for i18n string (can be literal or i18n keys).
   * These are merged with any runtime-provided params in error handler.
   */
  defaultI18nParams?: Record<string, string | number>;
}

/**
 * Central mapping of app error codes to notification/i18n/Sentry handling policies.
 * Each error code entry controls:
 *  - The i18n key used for message translation.
 *  - Notification type shown to user.
 *  - Whether to send error to Sentry by default.
 *  - (Optional) default parameters for localized message interpolation.
 *
 * DO NOT use notification.error/warning/info directly;
 * Always raise errors via error handler for consistency and audit.
 */
export const ERROR_HANDLING_CONFIG: Record<AppErrorCodeEnum, ErrorHandlingEntry> = {
  /**
   * Shown when attempting to unlock multiple password-protected files (not supported).
   * - Shown as a warning.
   * - Not sent to Sentry (user action, not system error).
   */
  [AppErrorCodeEnum.CannotUnlockMultiplePasswordProtected]: {
    i18nKey: 'errors.cannotUnlockMultiplePasswordProtected',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  /** Backend 400 INVALID_PARAMS on an Executive Report call: the offending filter is reset. */
  [AppErrorCodeEnum.DashboardInvalidFilter]: {
    i18nKey: 'dashboard.errors.invalidFilter',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  [AppErrorCodeEnum.DashboardPdfExportFailed]: {
    i18nKey: 'dashboard.errors.pdfExportFailed',
    notificationType: 'error',
    logToSentryByDefault: true,
  },
  /** 404 OBJECT_NOT_FOUND on a share-recipient drill-down: back to the list, refreshed. */
  [AppErrorCodeEnum.DashboardRecipientNotFound]: {
    i18nKey: 'dashboard.errors.recipientNotFound',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  [AppErrorCodeEnum.DashboardReportAccessDenied]: {
    i18nKey: 'dashboard.errors.accessDenied',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  [AppErrorCodeEnum.DashboardReportFetchFailed]: {
    i18nKey: 'errors.genericError',
    notificationType: 'error',
    logToSentryByDefault: true,
  },
  [AppErrorCodeEnum.DashboardReportServerError]: {
    i18nKey: 'dashboard.errors.serverError',
    notificationType: 'error',
    logToSentryByDefault: true,
  },
  /**
   * Lockout after too many password attempts on file fetch.
   * - Warning notification.
   * - Not sent to Sentry by default.
   */
  [AppErrorCodeEnum.FilesFetchPasswordLockout]: {
    i18nKey: 'errors.filePasswordLockout',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  /**
   * The user's session has expired.
   * - Warning notification.
   * - Not sent to Sentry by default (common and not actionable).
   */
  [AppErrorCodeEnum.SessionExpired]: {
    i18nKey: 'errors.sessionExpired',
    notificationType: 'warning',
    logToSentryByDefault: false,
  },
  /**
   * Generic error during file fetching.
   * - Shown as error (red notification).
   * - Sent to Sentry for devops tracing by default.
   * - Provides a default resource name for i18n interpolation.
   */
  [AppErrorCodeEnum.FilesFetchGeneric]: {
    i18nKey: 'errors.genericFetchError',
    notificationType: 'error',
    logToSentryByDefault: true,
    defaultI18nParams: { resource: 'common.files' },
  },
  /**
   * Admin transfer ownership (user files) failed.
   */
  [AppErrorCodeEnum.TransferOwnershipFailed]: {
    i18nKey: 'errors.transferOwnershipFailed',
    notificationType: 'error',
    logToSentryByDefault: true,
  },
};
